import React from 'react';

import { DateField } from '../../../components/datafields/DateField';
import { NumberField } from '../../../components/datafields/NumberField';
import { SelectField } from '../../../components/datafields/SelectField';
import { TextAreaField } from '../../../components/datafields/TextAreaField';
import { TextField } from '../../../components/datafields/TextField';
import { useFieldHighlight } from '../../../components/invoice-preview/SplitPanelBookingLayout';
import { LexOfficeBookingData } from '../../../types';
import {
  BOOKING_TYPE_OPTIONS,
  CURRENCY_OPTIONS,
} from '../constants/bookingFormConstants';
import {
  formatCurrency,
  formatDate,
  getBookingTypeLabel,
} from '../utils/formatters';

interface BookingFormFieldsProps {
  bookingData: LexOfficeBookingData;
  isEditing: boolean;
  onFieldChange: (
    field: keyof LexOfficeBookingData,
    value: LexOfficeBookingData[keyof LexOfficeBookingData]
  ) => void;
}

/**
 * Wrapper that highlights a field group when its bookingFieldMapping is active.
 * Uses role="group" since children may contain form inputs — a button semantic
 * would confuse screen readers. Click and focus events bubble up from children:
 * static text in view mode (click) and focusable inputs in edit mode (focus).
 * We rely on bubbling rather than giving the wrapper its own interactive role.
 */
const HighlightableField: React.FC<{
  fieldId: string;
  activeFieldId: string | null;
  onFieldFocus: (fieldId: string) => void;
  children: React.ReactNode;
}> = ({ fieldId, activeFieldId, onFieldFocus, children }) => {
  const isActive = activeFieldId === fieldId;
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={`rounded-md transition-all cursor-pointer ${
        isActive ? 'ring-2 ring-blue-400 bg-blue-50' : ''
      }`}
      onClick={() => onFieldFocus(fieldId)}
      onFocus={() => onFieldFocus(fieldId)}
    >
      {children}
    </div>
  );
};

const BookingFormFields: React.FC<BookingFormFieldsProps> = ({
  bookingData,
  isEditing,
  onFieldChange,
}) => {
  const highlight = useFieldHighlight();
  const activeFieldId = highlight?.activeFieldId ?? null;
  const noopFocus = React.useCallback((_fieldId: string) => undefined, []);
  const onFieldFocus = highlight?.onFieldFocus ?? noopFocus;

  return (
    <>
      {/* Responsive form layout: 1 column on mobile, 2 on tablet, 3 on desktop */}
      <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-2 text-sm'>
        {/* Column 1 */}
        <SelectField
          label='Type'
          value={bookingData.type}
          onChange={value =>
            onFieldChange(
              'type',
              value as 'purchaseinvoice' | 'purchasecreditnote'
            )
          }
          isEditing={isEditing}
          options={BOOKING_TYPE_OPTIONS}
          formatter={getBookingTypeLabel}
        />

        <HighlightableField
          fieldId='voucherNumber'
          activeFieldId={activeFieldId}
          onFieldFocus={onFieldFocus}
        >
          <TextField
            label='Voucher Number'
            value={bookingData.voucherNumber}
            onChange={value => onFieldChange('voucherNumber', value)}
            isEditing={isEditing}
          />
        </HighlightableField>

        <HighlightableField
          fieldId='contactName'
          activeFieldId={activeFieldId}
          onFieldFocus={onFieldFocus}
        >
          <TextField
            label='Contact'
            value={bookingData.contactName}
            onChange={value => onFieldChange('contactName', value)}
            isEditing={isEditing}
          />
        </HighlightableField>

        <HighlightableField
          fieldId='voucherDate'
          activeFieldId={activeFieldId}
          onFieldFocus={onFieldFocus}
        >
          <DateField
            label='Voucher Date'
            value={bookingData.voucherDate}
            onChange={value => onFieldChange('voucherDate', value)}
            isEditing={isEditing}
            formatter={formatDate}
          />
        </HighlightableField>

        <HighlightableField
          fieldId='dueDate'
          activeFieldId={activeFieldId}
          onFieldFocus={onFieldFocus}
        >
          <DateField
            label='Due Date'
            value={bookingData.dueDate}
            onChange={value => onFieldChange('dueDate', value)}
            isEditing={isEditing}
            formatter={formatDate}
          />
        </HighlightableField>

        <HighlightableField
          fieldId='totalGrossAmount'
          activeFieldId={activeFieldId}
          onFieldFocus={onFieldFocus}
        >
          <NumberField
            label='Total Gross'
            value={bookingData.totalGrossAmount}
            onChange={value => onFieldChange('totalGrossAmount', value)}
            isEditing={isEditing}
            formatter={amount => formatCurrency(amount, bookingData.currency)}
          />
        </HighlightableField>

        <HighlightableField
          fieldId='totalTaxAmount'
          activeFieldId={activeFieldId}
          onFieldFocus={onFieldFocus}
        >
          <NumberField
            label='Total Tax'
            value={bookingData.totalTaxAmount}
            onChange={value => onFieldChange('totalTaxAmount', value)}
            isEditing={isEditing}
            formatter={amount => formatCurrency(amount, bookingData.currency)}
          />
        </HighlightableField>

        <TextField label='Tax Type' value='Gross' isEditing={false} />

        <SelectField
          label='Currency'
          value={bookingData.currency || 'EUR'}
          onChange={value => onFieldChange('currency', value)}
          isEditing={isEditing}
          options={CURRENCY_OPTIONS}
        />
      </div>

      {/* Remark - Full width on all screen sizes */}
      <TextAreaField
        label='Remark'
        value={bookingData.remark}
        onChange={value => onFieldChange('remark', value)}
        isEditing={isEditing}
      />
    </>
  );
};

export default BookingFormFields;
